import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { HeroCustomFieldInput } from "@/components/hero/HeroCustomFieldInput";
import {
  entryKey,
  readHeroFieldFromProject,
  serializeHeroFieldValue,
  sortHeroFieldEntries,
  type HeroCustomFieldEntry,
} from "@/components/hero/heroCustomFieldUtils";
import type { Order } from "@/lib/projects";
import {
  getProjectCustomFieldSchemas,
  type HeroCustomFieldSchema,
} from "@/lib/projects";
import { updateOrderHeroCustomFields } from "@/lib/projectOverview";
import { isClaimedHeroCustomFieldLabel } from "@/lib/projectReferenzFields";
import { OverviewCard } from "@/projectOverview/OverviewCard";

export function HeroCustomFieldsSection({
  project,
  canEdit,
  onSaved,
}: {
  project: Order;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [schemas, setSchemas] = useState<HeroCustomFieldSchema[]>([]);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const entries = useMemo(() => {
    const out: HeroCustomFieldEntry[] = [];
    for (const schema of schemas) {
      for (const prop of schema.properties || []) {
        if (!prop.enabled) continue;
        // Bestellnummer/Kundenreferenz sind native Projektfelder.
        if (isClaimedHeroCustomFieldLabel(prop.label)) continue;
        out.push({ schemaId: schema.schemaId, property: prop });
      }
    }
    return sortHeroFieldEntries(out);
  }, [schemas]);

  useEffect(() => {
    void getProjectCustomFieldSchemas().then(setSchemas).catch(() => setSchemas([]));
  }, []);

  useEffect(() => {
    const next: Record<string, string | boolean> = {};
    for (const e of entries) {
      const key = entryKey(e);
      next[key] = readHeroFieldFromProject(
        project.hero_custom_fields,
        e.schemaId,
        e.property.uuid,
        e.property.type,
      );
    }
    setValues(next);
  }, [project, entries]);

  if (!entries.length) return null;

  async function handleSave() {
    setSaving(true);
    setMessage("");
    try {
      const fields = entries.map((e) => {
        const key = entryKey(e);
        return {
          schemaId: e.schemaId,
          propertyUuid: e.property.uuid,
          value: serializeHeroFieldValue(values[key], e.property.type),
        };
      });
      await updateOrderHeroCustomFields(project.id, fields);
      setMessage("Gespeichert.");
      onSaved();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  return (
    <OverviewCard title="Eigene Felder (Hero)">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {entries.map((e) => {
          const key = entryKey(e);
          const inputId = `po-hero-cf-${e.property.uuid}`;
          return (
            <div key={key}>
              <HeroCustomFieldInput
                property={e.property}
                inputId={inputId}
                value={values[key] ?? (e.property.type === "checkbox" ? false : "")}
                disabled={!canEdit || saving}
                onChange={(next) => setValues((prev) => ({ ...prev, [key]: next }))}
              />
            </div>
          );
        })}
      </div>
      {canEdit ? (
        <Button type="button" size="sm" className="mt-3" disabled={saving} onClick={() => void handleSave()}>
          Speichern
        </Button>
      ) : null}
      {message ? <p className="mt-2 text-xs text-label-tertiary">{message}</p> : null}
    </OverviewCard>
  );
}
